import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Home } from './pages/Home';
import { Room } from './pages/Room';
import { PrivacyPolicy } from './pages/legal/PrivacyPolicy';
import { TermsOfService } from './pages/legal/TermsOfService';
import { CookiePolicy } from './pages/legal/CookiePolicy';
import { useTelegramWebApp, getTelegramStartParam } from './hooks/useTelegramWebApp';

const AdminDashboard = lazy(() =>
  import('./pages/AdminDashboard').then(module => ({ default: module.AdminDashboard }))
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});


function TelegramStartRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const startParam = getTelegramStartParam();
    if (!startParam) return;
    if (location.pathname.startsWith('/room/')) return;
    navigate(`/room/${startParam}`, { replace: true });
  }, [location.pathname, navigate]);

  return null;
}

function App() {
  useTelegramWebApp();

  const handleRoomCreated = (roomId: string) => {
    window.location.href = `/room/${roomId}`;
  };

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <TelegramStartRedirect />
        <div className="min-w-0 w-full">
          <Routes>
            <Route path="/" element={<Home onRoomCreated={handleRoomCreated} />} />
            <Route path="/room/:roomId" element={<Room />} />
            <Route
              path="/admin"
              element={
                <Suspense fallback={<div className="min-h-screen bg-room-bg" />}>
                  <AdminDashboard />
                </Suspense>
              }
            />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
